import { createSignal, onCleanup, onMount } from 'solid-js'

const MIDNIGHT_REFRESH_DELAY_MS = 100

type LiveClockOptions = {
   /** Tick interval in milliseconds. */
   intervalMs: number
   /** Also refresh just after each local midnight, so day-based views roll over. */
   refreshAtMidnight?: boolean
}

/**
 * Returns the delay in milliseconds until just after the next local midnight.
 *
 * @param now - Reference time (defaults to now).
 * @returns The delay in milliseconds.
 */
function getNextLocalMidnightRefreshDelay(now = Date.now()) {
   const nextMidnight = new Date(now)
   nextMidnight.setDate(nextMidnight.getDate() + 1)
   nextMidnight.setHours(0, 0, 0, 0)
   return Math.max(0, nextMidnight.getTime() - now) + MIDNIGHT_REFRESH_DELAY_MS
}

/**
 * Creates a `now` signal that ticks on an interval while the owning component
 * is mounted, optionally re-firing just after each local midnight.
 *
 * @param options - Tick interval and midnight-refresh behaviour.
 * @returns The current-time accessor and a function to refresh it on demand.
 */
export function createLiveClock(options: LiveClockOptions) {
   const [now, setNow] = createSignal(Date.now())
   const refresh = () => setNow(Date.now())

   onMount(() => {
      refresh()
      const intervalId = window.setInterval(refresh, options.intervalMs)
      let midnightTimerId: number | undefined
      /**
       * Schedules a refresh just after the next local midnight.
       */
      const refreshAtNextMidnight = () => {
         midnightTimerId = window.setTimeout(() => {
            refresh()
            refreshAtNextMidnight()
         }, getNextLocalMidnightRefreshDelay())
      }
      if (options.refreshAtMidnight) {
         refreshAtNextMidnight()
      }
      onCleanup(() => {
         window.clearInterval(intervalId)
         if (midnightTimerId !== undefined) {
            window.clearTimeout(midnightTimerId)
         }
      })
   })

   return [now, refresh] as const
}
